import styles from 'LandingSite/layout/Pricing/Pricing.module.scss';
import React, {useEffect, useState} from 'react';
import Button from "components/Button/Buttons";
import {Icons} from "utils/Icons";
import {userStore} from "stores";
import {observer} from "mobx-react";
import {Helmet} from "react-helmet";
import {useNavigation} from "hooks/Navigation";
import IconListItem from "components/IconListItem/IconListItem";
import imgSrc from "images/Illustrations/explore.svg";
import FeatureWebService from "service/web/FeatureWebService";
import F from "utils/F";

const Pricing = observer(() => {

    const [pricing, setPricing] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        FeatureWebService.getPricing().then(setPricing);
    }, []);

    const navigation = useNavigation();

    const showContact = () => navigation.goTo('/contact');
    const showRegister = () => navigation.goTo('/register');
    // The subscribe + partner flows live inside the account area (Upgrade screen); send guests to
    // register first, existing users straight there.
    const goToUpgrade = () => userStore.isUser() ? navigation.goToUserPath('upgrade') : showRegister();

    // Premium price is the single source of truth from the backend (ADR 0005), not hardcoded here.
    const premiumPrice = pricing && pricing.monthlyAmount != null
        ? F.formatMoney(pricing.monthlyAmount, pricing.currency)
        : '';

    const renderPriceTier = (p) => {
        const user = userStore.isUser();

        return (
            <div key={p.title} className={`${styles.item} ${p.highlight ? styles.highlight || '' : ''}`}>
                <div className={styles.cardHeading}>
                    <h5>{p.title}</h5>
                    <span className={styles.headingInfo}>{p.description}</span>
                </div>
                <div className={styles.price}>
                    <h3>{p.price}{p.priceSuffix && <small>{p.priceSuffix}</small>}</h3>
                </div>
                <span>{p.priceInfo}</span>
                {p.action === 'REGISTER' &&
                    <Button style={{width: '100%', marginTop: 24}} buttonId={'pricing-free-sign-up'}
                            onClick={user ? () => navigation.goToUserPath('manage-account') : showRegister}
                            title={user ? 'Go to my account' : 'Get started free'}/>
                }
                {p.action === 'SUBSCRIBE' &&
                    <Button style={{width: '100%', marginTop: 24}} buttonId={'pricing-premium-subscribe'}
                            onClick={goToUpgrade}
                            title={user ? 'Upgrade to premium' : 'Get started'}/>
                }
                {p.action === 'PARTNER' &&
                    <Button isSecondary style={{width: '100%', marginTop: 24}} buttonId={'pricing-partner-apply'}
                            onClick={goToUpgrade}
                            title={'Apply to become a partner'}/>
                }
                <br/>
                <div className={styles.features}>
                    {p.featuresHeading && <h5>{p.featuresHeading}</h5>}
                    {p.features.map(feature => <IconListItem key={feature} icon={Icons.check()} text={feature}/>)}
                </div>
                {p.note && <span className={styles.headingInfo}>{p.note}</span>}
                <br/>
            </div>
        )
    }

    const free = {
        title: 'Free',
        description: 'Everything you need to get started.',
        price: 'Free',
        priceInfo: 'No card required',
        features: [
            'Create and manage your contracts',
            'Legally-binding e-signatures included',
            'Email notifications and reminders',
            'Secure storage for your contracts',
        ],
        note: 'Free accounts have some limits — upgrade any time for unlimited workflows.',
        action: 'REGISTER',
    }
    const premium = {
        title: 'Premium',
        description: 'Unlimited workflows for growing businesses.',
        highlight: true,
        price: premiumPrice || 'Premium',
        priceSuffix: premiumPrice ? ' /month' : '',
        priceInfo: 'Billed monthly, cancel anytime',
        featuresHeading: 'Everything in Free, PLUS',
        features: [
            'Unlimited workflows and contracts',
            'Unlimited e-signatures',
            'All contract types, including new workflows as they launch',
            'Audit trail for full compliance',
        ],
        action: 'SUBSCRIBE',
    }
    const partner = {
        title: 'Partner',
        description: 'For qualifying partner organisations.',
        price: 'Free lifetime',
        priceInfo: 'Subject to approval',
        featuresHeading: 'Everything in Premium, PLUS',
        features: [
            'Free lifetime premium',
            'Priority partner onboarding',
        ],
        action: 'PARTNER',
    }
    return (
        <>
            <Helmet>
                <title>Acculex | Pricing</title>
                <meta name='description'
                      content='Simple, transparent pricing. Start free, upgrade to premium for unlimited
                      workflows, or apply to become a partner. All accounts include contract storage and
                      electronic signatures.'/>
            </Helmet>
            <div className={styles.hero}>
                <div className={styles.heroContent}>
                    <div className={styles.heroText}>
                        <h1 className={styles.blockHeading}>Flexible and affordable.</h1>
                        <h5>Start free. Monthly billing, cancel anytime.</h5>
                    </div>
                        <img className={styles.heroImage} src={imgSrc} alt={'explore our pricing'}/>
                </div>
            </div>
            <div className={styles.pricing}>
                <h1>Which plan is right for you?</h1>
                <div className={styles.content}>
                    {renderPriceTier(free)}
                    {renderPriceTier(premium)}
                    {renderPriceTier(partner)}
                </div>
                <p style={{marginTop: '32px'}}>
                    Partner organisations may qualify for free lifetime premium — <a href={'/contact'}
                    onClick={(e) => { e.preventDefault(); showContact(); }}
                    style={{cursor: 'pointer', textDecoration: 'underline'}}>get in touch</a> to find out more.
                </p>
            </div>
        </>
    );
});

export default Pricing;
